import React, { useEffect, useMemo, useState } from "react";
import { useCompany } from "../../context/CompanyContext";
import { getPayrollReports } from "../payrollService";

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function PayrollReports() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");
  const [data, setData] = useState({
    runs: [],
    employees: [],
    payslips: [],
    anomalies: [],
    payments: [],
    loans: [],
    leaveRequests: [],
  });

  useEffect(() => {
    async function load() {
      if (!companyId) return;
      setBusy(true);
      setMsg("");
      try {
        const reportData = await getPayrollReports(companyId);
        setData(reportData);
      } catch (err) {
        setMsg(err.message || "Failed to load payroll reports");
      } finally {
        setBusy(false);
      }
    }
    load();
  }, [companyId]);

  const stats = useMemo(() => {
    const totalGross = data.runs.reduce(
      (sum, row) => sum + Number(row.gross_total || 0),
      0
    );
    const totalNet = data.runs.reduce(
      (sum, row) => sum + Number(row.net_total || 0),
      0
    );
    const totalDeductions = data.runs.reduce(
      (sum, row) => sum + Number(row.deduction_total || 0),
      0
    );
    const totalEmployerCost = data.runs.reduce(
      (sum, row) => sum + Number(row.employer_contribution_total || 0),
      0
    );
    const totalPayments = data.payments.reduce(
      (sum, row) => sum + Number(row.total_amount || 0),
      0
    );

    return {
      totalGross,
      totalNet,
      totalDeductions,
      totalEmployerCost,
      totalPayments,
      activeEmployees: data.employees.filter(
        (e) => e.employment_status === "active"
      ).length,
      openAnomalies: data.anomalies.filter((a) => !a.resolved).length,
      activeLoans: data.loans.filter((l) => l.status === "active").length,
      approvedLeave: data.leaveRequests.filter((l) => l.status === "approved")
        .length,
    };
  }, [data]);

  const recentRuns = useMemo(() => [...data.runs].slice(0, 5), [data.runs]);

  const recentPayments = useMemo(
    () => [...data.payments].slice(0, 5),
    [data.payments]
  );

  const recentAnomalies = useMemo(
    () => [...data.anomalies].slice(0, 5),
    [data.anomalies]
  );

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert error">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Total Gross Pay</p>
          <h3>{busy ? "..." : formatMoney(stats.totalGross)}</h3>
          <span>Across all payroll runs</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Total Net Pay</p>
          <h3>{busy ? "..." : formatMoney(stats.totalNet)}</h3>
          <span>Net paid to employees</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Total Deductions</p>
          <h3>{busy ? "..." : formatMoney(stats.totalDeductions)}</h3>
          <span>Taxes, pensions, loans and more</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Employer Contributions</p>
          <h3>{busy ? "..." : formatMoney(stats.totalEmployerCost)}</h3>
          <span>Additional employer cost</span>
        </div>
      </div>

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Active Employees</p>
          <h3>{busy ? "..." : stats.activeEmployees}</h3>
          <span>Eligible workforce</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Payment Batches</p>
          <h3>{busy ? "..." : data.payments.length}</h3>
          <span>Total generated batches</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Open Anomalies</p>
          <h3>{busy ? "..." : stats.openAnomalies}</h3>
          <span>Items needing review</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Active Loans</p>
          <h3>{busy ? "..." : stats.activeLoans}</h3>
          <span>Employee loans in cycle</span>
        </div>
      </div>

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <div>
              <h3>Recent Payroll Runs</h3>
              <p>Latest payroll processing activity.</p>
            </div>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Run Name</th>
                  <th>Status</th>
                  <th>Employees</th>
                  <th>Gross</th>
                  <th>Net</th>
                </tr>
              </thead>
              <tbody>
                {recentRuns.map((row) => (
                  <tr key={row.id}>
                    <td>{row.run_name}</td>
                    <td>
                      <span className={`pill ${row.status}`}>{row.status}</span>
                    </td>
                    <td>{Number(row.employee_count || 0).toLocaleString()}</td>
                    <td>{formatMoney(row.gross_total)}</td>
                    <td>{formatMoney(row.net_total)}</td>
                  </tr>
                ))}
                {!recentRuns.length && !busy && (
                  <tr>
                    <td colSpan="5">No payroll runs found.</td>
                  </tr>
                )}
                {busy && (
                  <tr>
                    <td colSpan="5">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <div>
              <h3>Recent Payment Batches</h3>
              <p>Latest payment generation activity.</p>
            </div>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Batch Name</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Payment Date</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentPayments.map((row) => (
                  <tr key={row.id}>
                    <td>{row.batch_name}</td>
                    <td>{row.payment_method}</td>
                    <td>
                      <span className={`pill ${row.status}`}>{row.status}</span>
                    </td>
                    <td>{row.payment_date}</td>
                    <td>{formatMoney(row.total_amount)}</td>
                  </tr>
                ))}
                {!recentPayments.length && !busy && (
                  <tr>
                    <td colSpan="5">No payment batches found.</td>
                  </tr>
                )}
                {busy && (
                  <tr>
                    <td colSpan="5">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <div>
              <h3>Recent Anomalies</h3>
              <p>Issues flagged during payroll processing.</p>
            </div>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Severity</th>
                  <th>Message</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentAnomalies.map((row) => (
                  <tr key={row.id}>
                    <td>{row.anomaly_type}</td>
                    <td>
                      <span className={`pill ${row.severity}`}>
                        {row.severity}
                      </span>
                    </td>
                    <td>{row.anomaly_message}</td>
                    <td>
                      <span
                        className={`pill ${
                          row.resolved ? "approved" : "processing"
                        }`}
                      >
                        {row.resolved ? "resolved" : "open"}
                      </span>
                    </td>
                  </tr>
                ))}
                {!recentAnomalies.length && !busy && (
                  <tr>
                    <td colSpan="4">No anomalies found.</td>
                  </tr>
                )}
                {busy && (
                  <tr>
                    <td colSpan="4">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <div>
              <h3>Operational Summary</h3>
              <p>High-level payroll operational indicators.</p>
            </div>
          </div>

          <div className="payroll-grid payroll-grid-2">
            <div className="payroll-mini-stat">
              <strong>Total Payments Generated</strong>
              <span>{formatMoney(stats.totalPayments)}</span>
            </div>
            <div className="payroll-mini-stat">
              <strong>Approved Leave Requests</strong>
              <span>{stats.approvedLeave}</span>
            </div>
            <div className="payroll-mini-stat">
              <strong>Payslips Created</strong>
              <span>{data.payslips.length}</span>
            </div>
            <div className="payroll-mini-stat">
              <strong>Total Employees</strong>
              <span>{data.employees.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
